import React, { useMemo, useRef, useState } from 'react';

import { exportToCsv } from 'shared/export/csv';
import { exportToExcel } from 'shared/export/excel';
import { exportToPdf } from 'shared/export/pdf';
import { exportTimestamp, exportedOnText } from 'shared/export/stamp';

import './HeatmapTable.scss';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface HeatmapRow {
  /** Row label (e.g. a district name). */
  label: string;
  /** One value per column, aligned with `columns`. */
  values: number[];
}

/** Toolbar icon button — same shape as AppChart's ChartTool. */
export interface HeatmapTool {
  icon: string; // PrimeIcons class
  label: string;
  onClick?: () => void;
}

export interface HeatmapTableProps {
  /** Card title. */
  title: string;
  /** Small hint next to the title. */
  hint?: string;
  /** Leading PrimeIcons class. */
  icon?: string;
  /** Column headers (e.g. subjects). */
  columns: string[];
  /** Row data. */
  rows: HeatmapRow[];
  /** Fixed color-scale domain. Defaults to min/max of the data. */
  min?: number;
  max?: number;
  /** Suffix appended to cell values / legend (e.g. "%"). */
  valueSuffix?: string;
  /** Number of decimals shown in cells. Default 1. */
  decimals?: number;
  /**
   * Toolbar buttons (Refresh / Table View / Download). Recognised by label:
   * a button labelled "Refresh", "Table"/"Table View", or
   * "Download"/"Export" is auto-wired to the built-in handler unless it
   * carries its own `onClick`. Omit for no toolbar.
   */
  tools?: HeatmapTool[];
  /** Base file name for CSV / Excel / PDF exports. */
  exportFileName?: string;
  /**
   * Called with a row's label when its name is clicked — makes district /
   * block / school rows clickable for drill-down. Omit to render plain,
   * non-interactive row labels.
   */
  onRowClick?: (label: string) => void;
  /** Extra class names. */
  className?: string;
}

// ─────────────────────────────────────────────
// Color scale (red → yellow → green, low → high)
// ─────────────────────────────────────────────

const STOPS = [
  { t: 0, c: [237, 137, 54] }, // #ed8936 orange (low)
  { t: 0.5, c: [250, 204, 21] }, // #facc15 amber/yellow (mid)
  { t: 1, c: [34, 139, 69] }, // #228b45 green (high)
];

const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);

const colorFor = (value: number, min: number, max: number): string => {
  if (max <= min) return 'rgb(254, 224, 139)';
  const t = Math.min(1, Math.max(0, (value - min) / (max - min)));
  // Find the two stops surrounding t.
  let lo = STOPS[0];
  let hi = STOPS[STOPS.length - 1];
  for (let i = 0; i < STOPS.length - 1; i++) {
    if (t >= STOPS[i].t && t <= STOPS[i + 1].t) {
      lo = STOPS[i];
      hi = STOPS[i + 1];
      break;
    }
  }
  const span = hi.t - lo.t || 1;
  const lt = (t - lo.t) / span;
  const r = lerp(lo.c[0], hi.c[0], lt);
  const g = lerp(lo.c[1], hi.c[1], lt);
  const b = lerp(lo.c[2], hi.c[2], lt);
  return `rgb(${r}, ${g}, ${b})`;
};

// Choose readable text color for a given cell background.
const textOn = (value: number, min: number, max: number): string => {
  const t = max <= min ? 0.5 : (value - min) / (max - min);
  // The orange (low) and green (high) ends are saturated → white text reads.
  // The amber/yellow middle is light → dark text. The window is widened to
  // 0.28–0.72 because the mid stop is now a brighter yellow.
  return t > 0.28 && t < 0.72 ? '#3f3f46' : '#ffffff';
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

const HeatmapTable: React.FC<HeatmapTableProps> = ({
  title,
  hint,
  icon = 'pi pi-table',
  columns,
  rows,
  min,
  max,
  valueSuffix = '',
  decimals = 1,
  tools,
  exportFileName = 'heatmap',
  onRowClick,
  className = '',
}) => {
  const [hover, setHover] = useState<string | null>(null);
  // Value of the cell currently hovered — drives the moving legend marker so
  // the user sees exactly where that cell sits on the colour range.
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  // Table View toggle (plain grid in place of the heatmap).
  const [tableView, setTableView] = useState(false);
  // Bumping this key remounts the grid, re-running the cell fade-in — the
  // same "refresh" affordance the charts give.
  const [refreshKey, setRefreshKey] = useState(0);
  // Download menu open state.
  const [downloadOpen, setDownloadOpen] = useState(false);

  // ── Export: flatten { label, values[] } + columns[] into row objects ──
  const exportColumns = useMemo(
    () => [
      { key: 'label', label: 'Name' },
      ...columns.map((c) => ({ key: c, label: c })),
    ],
    [columns]
  );
  const exportData = useMemo(
    () =>
      rows.map((r) => {
        const o: Record<string, unknown> = { label: r.label };
        columns.forEach((c, i) => {
          o[c] = r.values[i] ?? '';
        });
        return o;
      }),
    [rows, columns]
  );
  // Stamped base name shown in the download modal — mirrors AppChart so the
  // user sees the export date + time are baked into the file name.
  const exportBaseName = () => `${exportFileName}-${exportTimestamp()}`;
  const runExport = (kind: 'excel' | 'csv' | 'pdf') => {
    const fileName = exportBaseName();
    const args = { data: exportData, columns: exportColumns, fileName, title };
    if (kind === 'excel') exportToExcel(args);
    else if (kind === 'csv') exportToCsv(args);
    else exportToPdf(args);
    setDownloadOpen(false);
  };

  // Resolve each tool to a handler by its label, unless it carries its own.
  const resolvedTools = useMemo(
    () =>
      (tools ?? []).map((t) => {
        if (t.onClick) return t;
        const l = t.label.toLowerCase();
        if (l.includes('refresh')) {
          return { ...t, onClick: () => setRefreshKey((k) => k + 1) };
        }
        if (l.includes('table')) {
          return { ...t, onClick: () => setTableView((v) => !v) };
        }
        if (l.includes('download') || l.includes('export')) {
          return { ...t, onClick: () => setDownloadOpen((v) => !v) };
        }
        return t;
      }),
    [tools]
  );

  const [domainMin, domainMax] = useMemo(() => {
    if (min != null && max != null) return [min, max];
    const all = rows.flatMap((r) => r.values).filter((v) => Number.isFinite(v));
    const lo = min ?? (all.length ? Math.min(...all) : 0);
    const hi = max ?? (all.length ? Math.max(...all) : 100);
    return [lo, hi];
  }, [rows, min, max]);

  // Label precision follows the cell decimals, so a 0.3–2.9 range shows
  // "0.3 … 2.9" rather than being rounded to a flat "0 … 3".
  const fmtEnd = (v: number) => v.toFixed(decimals);
  const legendMid = ((domainMin + domainMax) / 2).toFixed(decimals);

  // 0–100% position of the hovered value along the domain, for the marker.
  const hoverPct =
    hoverValue != null && domainMax > domainMin
      ? Math.min(
          100,
          Math.max(
            0,
            ((hoverValue - domainMin) / (domainMax - domainMin)) * 100
          )
        )
      : null;

  // Flat list of every cell's key + value, for the legend's reverse lookup
  // below (legend → nearest cell), rebuilt whenever the data changes.
  const flatCells = useMemo(
    () =>
      rows.flatMap((row) =>
        row.values.map((v, ci) => ({
          key: `${row.label}-${columns[ci] ?? ci}`,
          value: v,
        }))
      ),
    [rows, columns]
  );

  // Reverse of the cell → legend direction above: hovering the legend's
  // gradient track maps the cursor's position back to a data value, finds
  // the cell whose value is closest to it, and highlights that cell —
  // mirroring hovering a cell directly, just driven from the legend side.
  const legendTrackRef = useRef<HTMLSpanElement>(null);
  const handleLegendHover = (e: React.MouseEvent<HTMLSpanElement>) => {
    const track = legendTrackRef.current;
    if (!track || domainMax <= domainMin || flatCells.length === 0) return;
    const rect = track.getBoundingClientRect();
    const pct = Math.min(
      1,
      Math.max(0, (e.clientX - rect.left) / (rect.width || 1))
    );
    const pointValue = domainMin + pct * (domainMax - domainMin);
    let nearest = flatCells[0];
    let bestDiff = Math.abs(nearest.value - pointValue);
    for (const cell of flatCells) {
      const diff = Math.abs(cell.value - pointValue);
      if (diff < bestDiff) {
        bestDiff = diff;
        nearest = cell;
      }
    }
    setHover(nearest.key);
    setHoverValue(nearest.value);
  };
  const handleLegendLeave = () => {
    setHover(null);
    setHoverValue(null);
  };

  return (
    <div className={`heatmap ${className}`.trim()}>
      <div className="heatmap__head">
        <div className="heatmap__title-wrap">
          <i className={`heatmap__icon ${icon}`} />
          <h6 className="heatmap__title">{title}</h6>
          {hint && <span className="heatmap__hint">{hint}</span>}
        </div>

        {resolvedTools.length > 0 && (
          <div className="heatmap__actions">
            <div className="heatmap__toolbar">
              {resolvedTools.map((t) => {
                const l = t.label.toLowerCase();
                const isActive =
                  (l.includes('table') && tableView) ||
                  ((l.includes('download') || l.includes('export')) &&
                    downloadOpen);
                return (
                  <button
                    key={t.label}
                    type="button"
                    className={`heatmap__tool${isActive ? ' is-active' : ''}`}
                    aria-label={t.label}
                    data-tip={t.label}
                    onClick={t.onClick}
                  >
                    <i className={t.icon} />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Download modal — same look & feel as AppChart's download popup ── */}
      {downloadOpen && (
        <div
          className="chart-download"
          role="dialog"
          aria-modal="true"
          aria-label="Download table"
          onClick={() => setDownloadOpen(false)}
        >
          <div
            className="chart-download__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="chart-download__header">
              <span className="chart-download__header-title">
                <i className="pi pi-download" />
                Download Table
              </span>
              <button
                type="button"
                className="chart-download__close"
                aria-label="Close"
                onClick={() => setDownloadOpen(false)}
              >
                <i className="pi pi-times" />
              </button>
            </div>

            <div className="chart-download__body">
              <p className="chart-download__file-label">FILE NAME</p>
              <p className="chart-download__file-name">{exportBaseName()}</p>
              <p className="chart-download__file-note">
                <i className="pi pi-clock" /> {exportedOnText()}
              </p>

              <div className="chart-download__options">
                <button
                  type="button"
                  className="chart-download__option chart-download__option--pdf"
                  onClick={() => runExport('pdf')}
                >
                  <span className="chart-download__badge chart-download__badge--pdf">
                    <i className="pi pi-file-pdf" />
                  </span>
                  <span className="chart-download__type">PDF</span>
                  <span className="chart-download__ext">.pdf</span>
                </button>

                <button
                  type="button"
                  className="chart-download__option chart-download__option--excel"
                  onClick={() => runExport('excel')}
                >
                  <span className="chart-download__badge chart-download__badge--excel">
                    <i className="pi pi-file-excel" />
                  </span>
                  <span className="chart-download__type">Excel</span>
                  <span className="chart-download__ext">.xlsx</span>
                </button>

                <button
                  type="button"
                  className="chart-download__option chart-download__option--csv"
                  onClick={() => runExport('csv')}
                >
                  <span className="chart-download__badge chart-download__badge--csv">
                    <i className="pi pi-file" />
                  </span>
                  <span className="chart-download__type">CSV</span>
                  <span className="chart-download__ext">.csv</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tableView ? (
        // ── Plain Table View (toggled from the toolbar) ──
        <div className="heatmap__scroll">
          <table className="heatmap__plain-table">
            <thead>
              <tr>
                <th className="is-left">Name</th>
                {columns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <th className="is-left" scope="row">
                    {onRowClick ? (
                      <button
                        type="button"
                        className="heatmap__row-link"
                        onClick={() => onRowClick(row.label)}
                      >
                        {row.label}
                      </button>
                    ) : (
                      row.label
                    )}
                  </th>
                  {row.values.map((v, ci) => (
                    <td key={`${row.label}-${columns[ci] ?? ci}`}>
                      {v.toFixed(decimals)}
                      {valueSuffix}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="heatmap__scroll" key={refreshKey}>
          <table className="heatmap__table">
            <thead>
              <tr>
                <th className="heatmap__corner" />
                {columns.map((col) => (
                  <th key={col} className="heatmap__col-head">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <th className="heatmap__row-head" scope="row">
                    {onRowClick ? (
                      <button
                        type="button"
                        className="heatmap__row-link"
                        onClick={() => onRowClick(row.label)}
                      >
                        {row.label}
                      </button>
                    ) : (
                      row.label
                    )}
                  </th>
                  {row.values.map((v, ci) => {
                    const key = `${row.label}-${columns[ci] ?? ci}`;
                    return (
                      <td
                        key={key}
                        className={`heatmap__cell${hover === key ? ' is-hover' : ''}`}
                        style={{
                          background: colorFor(v, domainMin, domainMax),
                          color: textOn(v, domainMin, domainMax),
                        }}
                        title={`${row.label} · ${columns[ci] ?? ''}: ${v}${valueSuffix}`}
                        onMouseEnter={() => {
                          setHover(key);
                          setHoverValue(v);
                        }}
                        onMouseLeave={() => {
                          setHover(null);
                          setHoverValue(null);
                        }}
                      >
                        {v.toFixed(decimals)}
                        {valueSuffix}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend — one continuous gradient with a marker that tracks the
          hovered cell's value, so hovering shows where it falls on the range.
          Hidden in Table View (no colour scale there). */}
      {!tableView && (
        <div className="heatmap__legend">
          <div className="heatmap__legend-scale">
            <span
              className="heatmap__legend-track"
              ref={legendTrackRef}
              onMouseMove={handleLegendHover}
              onMouseLeave={handleLegendLeave}
            >
              {hoverPct != null && (
                <span
                  className="heatmap__legend-marker"
                  style={{ left: `${hoverPct}%` }}
                >
                  <span className="heatmap__legend-marker-value">
                    {hoverValue?.toFixed(decimals)}
                    {valueSuffix}
                  </span>
                </span>
              )}
            </span>
            <div className="heatmap__legend-ticks">
              <span className="heatmap__legend-end">
                {fmtEnd(domainMin)}
                {valueSuffix}
              </span>
              <span className="heatmap__legend-mid">
                {legendMid}
                {valueSuffix}
              </span>
              <span className="heatmap__legend-end">
                {fmtEnd(domainMax)}
                {valueSuffix}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeatmapTable;
